import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { driverApi } from "../services/driverApi";
import logo from "../assets/logo.png";

const PRIMARY_NAV = [
  {
    to: "/enrollme/driver/dashboard",
    label: "Tasks",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  },
  {
    to: "/enrollme/driver/earnings",
    label: "Earnings",
    icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  {
    to: "/enrollme/driver/messages",
    label: "Messages",
    icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  },
  {
    to: "/enrollme/driver/uploads",
    label: "Docs",
    icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12",
  },
];

const SECONDARY_NAV = [
  {
    to: "/enrollme/driver/profile",
    label: "Profile",
    icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  },
  {
    to: "/enrollme/driver/sign",
    label: "Sign Documents",
    icon: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z",
  },
  {
    to: "/enrollme/driver/wc43",
    label: "WC43 Form",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
];

export default function DriverLayout({ children, driver, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    driverApi.getSettings().then((d) => setCompanyName(d.settings?.companyName || d.settings?.companyTradeName || "")).catch(() => {});
  }, []);

  async function handleLogout() {
    try { await driverApi.logout(); } catch {}
    setMoreOpen(false);
    onLogout();
    navigate("/enrollme/driver/login");
  }

  const secondaryActive = SECONDARY_NAV.some((n) => location.pathname === n.to);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={logo} alt="TaxiOps" className="h-8 w-8 object-contain flex-shrink-0" />
            <div>
              <p className="text-[10px] text-blue-600 font-semibold uppercase tracking-wider leading-tight">Driver Portal</p>
              <p className="text-sm font-bold text-gray-900 leading-tight">{companyName}</p>
            </div>
          </div>
          {driver && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-blue-600">
                  {(driver.firstName?.[0] ?? "") + (driver.lastName?.[0] ?? "")}
                </span>
              </div>
              <div className="text-right hidden xs:block">
                <p className="text-xs font-medium text-gray-800 leading-tight">{driver.firstName} {driver.lastName}</p>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Page content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {children}
      </main>

      {/* Bottom spacer so content clears the nav */}
      <div className="h-20" />

      {/* More sheet backdrop */}
      {moreOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setMoreOpen(false)}
        />
      )}

      {/* More sheet — anchored at bottom-0 so translate-y-full hides it fully off-screen */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 transition-transform duration-300 ease-out ${moreOpen ? "translate-y-0" : "translate-y-full"}`}
        style={{ maxWidth: 512, margin: "0 auto" }}
      >
        <div
          className="bg-white rounded-t-2xl shadow-xl overflow-hidden"
          style={{ paddingBottom: "calc(4rem + env(safe-area-inset-bottom))" }}
        >
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>

          <div className="px-4 pb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">More</p>
            <ul className="divide-y divide-gray-100">
              {SECONDARY_NAV.map(({ to, label, icon }) => {
                const active = location.pathname === to;
                return (
                  <li key={to}>
                    <Link
                      to={to}
                      onClick={() => setMoreOpen(false)}
                      className={`flex items-center gap-3 px-2 py-3.5 rounded-xl transition ${active ? "text-blue-600" : "text-gray-700 hover:bg-gray-50"}`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? "bg-blue-50" : "bg-gray-100"}`}>
                        <svg className={`w-5 h-5 ${active ? "text-blue-600" : "text-gray-500"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.2 : 1.8} d={icon} />
                        </svg>
                      </div>
                      <span className={`text-sm font-medium ${active ? "text-blue-600" : "text-gray-800"}`}>{label}</span>
                      {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Logout */}
            <div className="mt-2 pt-3 border-t border-gray-100 pb-2">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-2 py-3 rounded-xl text-red-600 hover:bg-red-50 transition"
              >
                <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <span className="text-sm font-medium">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom nav bar — z-30 so the backdrop (z-40) covers it when More is open */}
      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 z-30" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="max-w-2xl mx-auto flex items-stretch h-16">
          {PRIMARY_NAV.map(({ to, label, icon }) => {
            const active = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 transition"
              >
                <div className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition ${active ? "bg-blue-50" : ""}`}>
                  <svg className={`w-5 h-5 ${active ? "text-blue-600" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.2 : 1.8} d={icon} />
                  </svg>
                  <span className={`text-[10px] font-semibold leading-none ${active ? "text-blue-600" : "text-gray-400"}`}>{label}</span>
                </div>
              </Link>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setMoreOpen((o) => !o)}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 transition"
          >
            <div className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition ${secondaryActive || moreOpen ? "bg-blue-50" : ""}`}>
              <svg className={`w-5 h-5 ${secondaryActive || moreOpen ? "text-blue-600" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
              </svg>
              <span className={`text-[10px] font-semibold leading-none ${secondaryActive || moreOpen ? "text-blue-600" : "text-gray-400"}`}>More</span>
            </div>
          </button>
        </div>
      </nav>
    </div>
  );
}
